import { queryOptions } from "@tanstack/react-query";

import { getAllRelationshipsForSchema } from "~/api/generated/schema-registry/schema-registry/schema-registry";
import { hasApiErrorStatus } from "~/lib/apiClient";

import { ownerSchemasOf, type SchemaRef } from "./utils/schemaOwner";

// Schema relationships change only when someone edits the schema registry.
const OWNER_STALE_MS = 10 * 60_000;

/** Every schema that owns `schema`'s records (`ownerSchemasOf`); empty when it stands alone. */
export const schemaOwnersQueryOptions = (
  schema: string,
  version: string,
  tenantId: string | undefined,
) =>
  queryOptions({
    queryKey: ["navigation", "schema-owner", tenantId, schema, version] as const,
    queryFn: async ({ signal }): Promise<SchemaRef[]> => {
      try {
        const response = await getAllRelationshipsForSchema(schema, version, { signal });

        return ownerSchemasOf(response.data, schema);
      } catch (error) {
        // The registry answers 404 for a schema with no relationships at all.
        if (hasApiErrorStatus(error, 404)) {
          return [];
        }

        throw error;
      }
    },
    staleTime: OWNER_STALE_MS,
  });

/** The schema that owns `schema`'s records, or `null` when it stands alone. Same cache entry. */
export const schemaOwnerQueryOptions = (
  schema: string,
  version: string,
  tenantId: string | undefined,
) =>
  queryOptions({
    ...schemaOwnersQueryOptions(schema, version, tenantId),
    select: (owners: SchemaRef[]): SchemaRef | null => owners[0] ?? null,
  });
